"use client";
import React, { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const pakete = [
  {
    name: "Basic Build",
    price: "120",
    originalPrice: null,
    desc: "Perfekt für alle, die nur den Zusammenbau brauchen.",
    features: [
      "PC zusammenbauen",
      "Grundfunktion testen",
      "Sauberer Aufbau",
    ],
    cta: "Jetzt anfragen",
    featured: false,
    color: "from-white/5 to-white/[0.02]",
  },
  {
    name: "Setup Build",
    price: "180",
    originalPrice: "200",
    desc: "Der Allrounder – vollständig einsatzbereit aus der Box.",
    features: [
      "PC zusammenbauen",
      "Windows installieren (selbstbezahlt)",
      "Treiber installieren",
      "Updates durchführen",
      "Grundlegende Optimierung",
    ],
    cta: "Beliebteste Wahl",
    featured: true,
    color: "from-violet-500/10 to-blue-500/5",
  },
  {
    name: "Premium Build",
    price: "250",
    originalPrice: "300",
    desc: "Das komplette Paket für maximale Performance und Ästhetik.",
    features: [
      "PC zusammenbauen",
      "Windows + Treiber",
      "BIOS Update",
      "Kabelmanagement",
      "RGB Einrichtung",
      "Gaming Optimierung",
    ],
    cta: "Premium wählen",
    featured: false,
    color: "from-white/5 to-white/[0.02]",
  },
];

const extras = [
  { name: "BIOS Update", price: "+20 CHF", desc: "" },
  { name: "Reinigung / PC Cleaning", price: "+40 CHF", desc: "" },
  { name: "RGB / Lüfter Setup", price: "+25 CHF", desc: "" },
  { name: "Performance Optimierung", price: "+30 CHF", desc: "" },
  { name: "Windows Installation", price: "+30 CHF", desc: "Für Basic Build" },
  { name: "Hardware Einkauf Service", price: "ab 50 CHF", desc: "Komponenten auswählen & bestellen" },
];

export default function PreiseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    sectionRef?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="preise" ref={sectionRef} className="relative py-28 lg:py-36 px-6 lg:px-12 overflow-hidden">
      <div className="orb w-[500px] h-[500px] bg-violet-500/6 top-0 right-0" />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <div className="section-label justify-center mb-4">Preise</div>
          <h2 className="font-display font-extrabold text-[clamp(2rem,4vw,3.2rem)] text-white leading-tight mb-4">
            Klar. Fair.{" "}
            <span className="gradient-text">Transparent.</span>
          </h2>
          <p className="text-white/45 max-w-lg mx-auto text-sm leading-relaxed">
            Drei Pakete für jeden Bedarf – ohne versteckte Kosten.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {pakete?.map((p, i) => (
            <div
              key={p?.name}
              className={`pricing-card reveal-hidden relative ${p?.featured ? "featured" : ""}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {p?.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-violet-500 to-blue-500 text-white text-[10px] font-mono font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
                    Empfohlen
                  </div>
                </div>
              )}

              <div className={`absolute inset-0 rounded-lg bg-gradient-to-br ${p?.color} pointer-events-none`} />

              <div className="relative">
                <div className="mb-6">
                  <h3 className="font-display font-extrabold text-xl text-white mb-1">{p?.name}</h3>
                  <p className="text-white/40 text-xs">{p?.desc}</p>
                </div>

                <div className="flex items-end gap-1 mb-2">
                  <span className="font-display font-extrabold text-5xl text-white leading-none">{p?.price}</span>
                  <span className="font-mono text-sm text-white/40 mb-2">CHF</span>
                </div>
                {p?.originalPrice && (
                  <div className="mb-6">
                    <span className="font-mono text-sm text-white/30 line-through">{p?.originalPrice} CHF</span>
                    <span className="ml-2 text-[10px] font-mono text-green-400 uppercase tracking-widest">Rabatt</span>
                  </div>
                )}
                {!p?.originalPrice && <div className="mb-6" />}

                <ul className="space-y-3 mb-8">
                  {p?.features?.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-white/65">
                      <div className="w-4 h-4 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckIcon" size={10} className="text-violet-400" variant="solid" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href="#kontakt"
                  className={`block text-center py-3 px-6 rounded text-xs font-display font-bold uppercase tracking-widest transition-all duration-300 ${
                    p?.featured
                      ? "btn-primary" :"border border-white/15 text-white/70 hover:border-violet-500/50 hover:text-white hover:bg-violet-500/10"
                  }`}
                >
                  {p?.featured ? p?.cta : "Anfragen"}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Budget PC Build Special Card */}
        <div className="glass rounded-xl p-6 lg:p-8 mb-6 reveal-hidden border border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent" style={{ transitionDelay: "250ms" }}>
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="lg:w-48 flex-shrink-0">
              <span className="font-mono text-[10px] text-green-400 uppercase tracking-widest">Neu</span>
              <h4 className="font-display font-bold text-white mt-1">Budget PC Build</h4>
            </div>
            <div className="flex-1">
              <p className="text-white/50 text-sm leading-relaxed mb-5">
                Du gibst mir dein Gesamtbudget – ich wähle die passenden Komponenten aus, koordiniere die Bestellung und baue den PC für dich zusammen. Alles aus einer Hand.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white/[0.03] border border-green-500/20">
                  <div className="font-mono text-[10px] text-green-400 uppercase tracking-widest mb-1">Teile-Auswahl Service</div>
                  <div className="font-display font-extrabold text-3xl text-white">100 <span className="text-sm font-mono text-white/40">CHF</span></div>
                  <p className="text-xs text-white/35 mt-1">Komponenten-Auswahl, Preisvergleich & Bestellkoordination (z.B. Digitec/Galaxus)</p>
                </div>
                <div className="p-4 rounded-lg bg-white/[0.03] border border-violet-500/20">
                  <div className="font-mono text-[10px] text-violet-400 uppercase tracking-widest mb-1">+ Build-Paket</div>
                  <div className="font-display font-bold text-lg text-white/80">Basic / Setup / Premium</div>
                  <p className="text-xs text-white/35 mt-1">120 / 180 / 250 CHF – je nach gewünschtem Leistungsumfang</p>
                </div>
              </div>
              <p className="text-xs text-white/30 mt-4 leading-relaxed">
                <span className="text-green-400">Hinweis:</span> Du bezahlst die PC-Komponenten direkt beim Händler (z.B. Digitec/Galaxus). Die 100 CHF Service-Gebühr deckt die Teile-Auswahl und Bestellkoordination ab. Dazu kommt das gewählte Build-Paket für den Zusammenbau.
              </p>
            </div>
          </div>
        </div>

        {/* Extras */}
        <div className="glass rounded-xl p-6 lg:p-8 reveal-hidden" style={{ transitionDelay: "300ms" }}>
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="lg:w-48 flex-shrink-0">
              <span className="font-mono text-[10px] text-violet-400 uppercase tracking-widest">Zusatzleistungen</span>
              <h4 className="font-display font-bold text-white mt-1">Add-ons</h4>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
              {extras?.map((e) => (
                <div key={e?.name} className="flex flex-col gap-1 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                  <span className="text-xs text-white/50">{e?.name}</span>
                  {e?.desc && <span className="text-[10px] text-white/30">{e?.desc}</span>}
                  <span className="font-display font-bold text-sm text-violet-400">{e?.price}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Hardware Einkauf Service description */}
          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <p className="text-xs text-white/35 leading-relaxed">
              <span className="text-violet-400 font-semibold">Hardware Einkauf Service:</span>{" "}
              Auf Wunsch kann ich auch die passenden PC-Komponenten für dich auswählen und bestellen (z.B. Digitec, Galaxus oder andere Shops), solange sie verfügbar sind. So bekommst du ein perfekt abgestimmtes System ohne selbst alles recherchieren zu müssen.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-white/25 mt-6 font-mono">
          Preise können je nach Aufwand und System leicht variieren.
        </p>
      </div>
    </section>
  );
}